<template>
  <div class="relative">
    <label v-if="label" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
      {{ label }}
    </label>
    
    <div
      class="relative"
      @mouseenter="isHovered = true"
      @mouseleave="isHovered = false"
    >
      <input
        type="text"
        :ref="id ? id : 'searchSelectInput'"
        :value="selectedLabel || searchQuery"
        @input="handleInput"
        @keydown="handleKeydown"
        :id="id || 'id-' + Math.random().toString(36).substr(2, 16)"
        @focus="isOpen = true"
        @blur="handleBlur"
        :placeholder="
          isLoading
            ? 'Loading ... '
            : (!isEmpty(placeholder) ? placeholder : label) +
              (remote == true ? ' (type to load list)' : '')
        "
        :readonly="readonly"
        class="w-full px-3 py-2 rounded-lg border text-gray-900 dark:text-white bg-white dark:bg-gray-900"
        :class="[
          error
            ? 'border-red-300 dark:border-red-700'
            : 'border-gray-200 dark:border-gray-700',
          readonly ? 'bg-gray-50 dark:bg-gray-800 cursor-not-allowed' : '',
        ]"
        :title="selectedLabel"
      />

      <!-- Clear button -->
      <button
        ref="clearButton"
        v-if="selectedLabel && !readonly"
        @click="clearSelection"
        @mousedown.prevent
        class="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors bg-white dark:bg-gray-900"
      >
        <IconComponent name="Trash2" class="w-4 h-4" />
      </button>

      <!-- Dropdown arrow -->
      <span
        class="absolute right-3 bg-white dark:bg-gray-900 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
        v-if="!isOpen && !selectedLabel"
      >
        <IconComponent name="ChevronDown" />
      </span>
    </div>
    <p v-if="error" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ error }}</p>

    <template v-if="isOpen && !readonly">
      <div
        v-if="isLoading"
        class="p-4 text-center text-gray-500 dark:text-gray-400"
      >
        Loading...
      </div>

      <!-- No results -->
      <div
        v-else-if="filteredOptions.length === 0 && !showAddNew"
        class="p-4 text-center text-gray-500 dark:text-gray-400 z-[9000] w-full absolute bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 max-h-60 overflow-y-auto"
      >
        No results found
      </div>
      <div v-else class="z-[9000] w-full absolute bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 max-h-60 overflow-y-auto">
        <button
          v-for="(option, index) in filteredOptions"
          :key="option.id ?? 'default-key'"
          @click="handleSelect(option)"
          class="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700"
          :class="[
            option.id === modelValue
              ? 'bg-blue-50 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400'
              : 'text-gray-900 dark:text-white',
            index === focusedOptionIndex ? 'bg-gray-100 dark:bg-gray-700' : '',
          ]"
        >
          {{ option.name }}
        </button>
      </div>
      <div v-if="showAddNew" class="z-[9000] w-full absolute bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 max-h-60 overflow-y-auto">
      <button          
          @click.stop="handleAddNew"
          @mousedown.prevent
          class="w-full px-4 py-2 text-left text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/50 flex items-center gap-2 rounded-md shadow"
        >
          <IconComponent name="Plus"/>
          Add {{ searchQuery.length > 0 ? '"' + searchQuery + '"' : 'new ' + props.label }}
        </button>
        </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import {
  ref,
  computed,
  watch,
  defineAsyncComponent,
  createVNode,
  render,
  onMounted,
  nextTick,
  DefineComponent,
} from "vue";
import { isEmpty } from "../../../utils/CommonFunctions";
import { api } from "../../../api/Api";
import MessageBoxService from "../../../services/MessageBox";
import { Permission } from "../../../types/auth";
import { tokenManager } from "../../../api/utils/tokenManager";
import { listManager } from "../../../stores/listManager";
import fuzzySearch from "../../../utils/fuzzySearc";
import { LocalStorageFilterOptions } from "../../../types/models";
import { Params } from "../../table/table";
import IconComponent from "../common/IconComponent.vue";
//import customerGroups from "../../../features/revenue/masterdata/customertypes/CustomerTypesForm.vue";

const props = withDefaults(
  defineProps<{
    modelValue: string | number | boolean | Date | string[] | boolean[] | number[] | Date[] | null | undefined;
    options?: { id: string | number | null; name: string }[];
    label?: string;
    placeholder?: string;
    error?: string | null;
    id?: string | undefined;
    endPoint?: string;
    readonly?: boolean;
    allowNewRecord?: boolean;
    extra?: string | number;
    hasExtra?: boolean;
    extra1?: string | number;
    extra2?: string | number;
    extra3?: string | number;
    remove?: string | number;
    remote?: boolean;
    apiAction?: string;
    withDetails?: boolean;
    localStorage?: string;
    localStorageFilters?: LocalStorageFilterOptions[];
    fuzzy?: boolean;
    customUrl?: string;
    requireParams?: boolean;
    params?: Params[];
    returnValue?: any
  }>(),
  {
    apiAction: "list",
    withDetails: false,
  }
);

const selectLists = listManager();

const emit = defineEmits<{
  (e: "update:modelValue", value: string | number | null): void;
  (e: "blur"): void;
  (e: "select", option: any, value: any): void;
  (e: "loaded", data: any[]): void;
}>();

const isOpen = ref(false);
const searchQuery = ref("");
const selectedLabel = ref();
const isLoading = ref(false);
const isHovered = ref(false);
const items = ref<{ id: string | number | null; name: string }[]>([]);
const fullItems = ref<{ id: string | number | null; name: string }[]>([]);
const focusedOptionIndex = ref<number | null>(null);

const filteredOptions = computed(() => {
  if (!searchQuery.value) return items.value;

  const query = searchQuery.value.toLowerCase();
  return items.value.filter((option) =>
    option.name.toLowerCase().includes(query)
  );
});

const showAddNew = computed(() => {
  return (
    (props.allowNewRecord &&
      searchQuery.value &&
      filteredOptions.value.length === 0 &&
      !props.readonly) ||
    (props.allowNewRecord && items.value.length <= 0)
  );
});

const handleSelect = (option: { id: string | number | null; name: string }) => {
  emit("update:modelValue", option.id);
  const item = fullItems.value.find((opt) => opt["id"] === option["id"]);
  emit("select", item, props.returnValue);
  selectedLabel.value = option.name;
  searchQuery.value = "";
  isOpen.value = false;
};

const handleBlur = () => {
  setTimeout(() => {
    emit("blur");

    if (searchQuery.value && !selectedLabel.value) {
      searchQuery.value = "";
      emit("update:modelValue", "");
    } else if (searchQuery.value && props.modelValue) {
      const option = items.value.find((opt) => opt.id === props.modelValue);
      if (option) {
        selectedLabel.value = option.name;
        searchQuery.value = "";
      } else if (props.endPoint || props.customUrl) {
        loadOptions(props.modelValue);
      }
    }
    focusedOptionIndex.value = null;
    isOpen.value = false;
  }, 200);
};

const handleInput = (e: Event) => {
  if (props.readonly) return;

  const input = e.target as HTMLInputElement;
  const value = input.value;

  if (value === "") {
    emit("update:modelValue", "");
    selectedLabel.value = "";
  }

  searchQuery.value = value;
  isOpen.value = true;

  if (props.remote && value.length > 2) debouncedLoadOptions();
};

const handleKeydown = (e: KeyboardEvent) => {
  if (props.readonly) return;

  if (e.key === "Backspace" && !searchQuery.value && selectedLabel.value) {
    e.preventDefault();
    emit("update:modelValue", "");
    selectedLabel.value = "";
    isOpen.value = true;
  } else if (e.key === "ArrowDown") {
    e.preventDefault();
    if (focusedOptionIndex.value === null) {
      focusedOptionIndex.value = 0;
    } else if (focusedOptionIndex.value < filteredOptions.value.length - 1) {
      focusedOptionIndex.value += 1;
    }
  } else if (e.key === "ArrowUp") {
    e.preventDefault();
    if (focusedOptionIndex.value != null && focusedOptionIndex.value > 0) {
      focusedOptionIndex.value -= 1;
    }
  } else if (e.key === "Enter" && focusedOptionIndex.value !== null) {
    handleSelect(filteredOptions.value[focusedOptionIndex.value]);
  }
};

const clearSelection = (e?: MouseEvent) => {
  if (props.readonly) return;

  if (e) {
    e.stopPropagation();
  }
  emit("update:modelValue", "");
  emit("select", "", props.returnValue);
  selectedLabel.value = "";
  searchQuery.value = "";
  focusedOptionIndex.value = null;
};

// Expose clearSelection function
defineExpose({
  clearSelection,
});

const loadOptions = async (idValue: any = null) => {
  if (!props.endPoint && !props.localStorage && !props.customUrl) {
    items.value = props.options || [];
    fullItems.value = props.options || [];
    const option = items.value.find((opt) => opt.id === props.modelValue || opt.id?.toString() === props.modelValue?.toString());
    if (option) {
      selectedLabel.value = option.name;
    } else {
      selectedLabel.value = null;
    }
    return;
  }

  if (props.localStorage) {
    await selectLists
      .getList(props.localStorage, props.localStorageFilters, searchQuery.value)
      .then((response) => {
        if (props.fuzzy) {
          fuzzySearch(response, searchQuery.value).then((res: any) => {
            items.value = res;
            fullItems.value = res;
            emit("loaded", res);
          });
        } else {
          items.value = response;
          fullItems.value = response;
        }
        if (
          !items.value ||
          (items.value.length <= 0 && (props.endPoint || props.customUrl))
        ) {
          loadFromApi(idValue);
        }
      })
      .catch(() => {
        if (props.endPoint || props.customUrl) loadFromApi(idValue);
      });
    return;
  } else if (!props.remote && (props.endPoint || props.customUrl)) {
    loadFromApi(idValue);
  }
};

const loadFromApi = async (idValue: any = null) => {
  if (props.hasExtra && isEmpty(props.extra)) return;
  try {
    isLoading.value = true;
    const apiData: { [key: string]: any } = {
      extra: props.extra,
      extraid: props.extra,
      extra1: props.extra1,
      extra2: props.extra2,
      extra3: props.extra3,
      remove: props.remove,
      details: props.withDetails,
      search_query: searchQuery.value,
      id_value: idValue,
    };
    props.params?.forEach((p) => {
      apiData[p.field] = p.value;
    });
    const response = await api.post(
      props.endPoint
        ? props.endPoint +
            (props.endPoint.slice(-1) !== "/" ? "/" : "") +
            props.apiAction
        : props.customUrl || "",
      apiData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        skipLoading: true,
      }
    );
    fullItems.value = response.data;
    items.value = response.data.map((item: any) => ({
      id: item.id,
      name: item.name,
    }));
    if (props.modelValue) {
      const option = items.value.find((opt) => opt.id === props.modelValue);
      if (option) {
        selectedLabel.value = option.name;
      }
    }
    //updateSelectedItemFrommodal(props.modelValue);
    emit("loaded", response.data);
  } catch (error) {
    console.error("Error loading options:", error);
  } finally {
    isLoading.value = false;
  }
};

// Debounce function
function debounce(func: Function, wait: number) {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  return function (...args: any[]) {
    if (timeout !== null) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(() => func.apply(null, args), wait);
  };
}

// Debounce the loadOptions function
const debouncedLoadOptions = debounce(loadFromApi, 300);

const handleAddNew = async () => {
  try {
    // Dynamically import the component using the fixed path
    const comp_path = props.endPoint?.replace("/admin", "").replace(/-/g, "");
const comp =
  (comp_path?.slice(-1) === "/" ? "" : comp_path + "/") +
  props.label?.replace(/ /g, "") +
  (props.label?.slice(-1) === "s" ? "" : "s") +
  "Form.vue";
  const modalComponent = `/src/features/${comp}`;
  //const modalComponent = '/src/features/revenue/masterdata/customertypes/CustomerTypesForm.vue'

// Preload all modal components
const modalModules = import.meta.glob('@features/**/*Form.vue');
const FormModal = defineAsyncComponent(() => {
      const module = modalModules[modalComponent];
      if (!module) {
        MessageBoxService.show("Could not load modal component", "error");
        return import('../../../features/FallbackForm.vue') as unknown as Promise<DefineComponent>;
      }
      return module() as Promise<DefineComponent>;
    });

    // Create modal container
    const container = document.createElement("div");
    document.body.appendChild(container);

    const perm: Permission[] = tokenManager.getPermissions();
    const rpath = props.endPoint?.replace("clients/", "");
    const permissions = perm.filter(function (itm) {
      return itm.route_path == rpath;
    });
    // Create and mount modal
    const vnode = createVNode(FormModal, {
      permissions: permissions,
      title: props.label,
      endPoint: props.endPoint,
      mode: "create",
      listCreateName: searchQuery.value,
      onSave: async (newRecord: any) => {
        const newOption = {
          id: newRecord.id.toString(),
          name: newRecord.name,
        };
        fullItems.value.push(newRecord)
        items.value = [...items.value, newOption];

        // Select the new record
        //await nextTick();
        emit("update:modelValue", newOption.id);
  emit("select", newRecord, props.returnValue);
  selectedLabel.value = newOption.name;
  searchQuery.value = "";
  isOpen.value = false;
        //handleSelect(newOption);

        // Remove modal
        render(null, container);
        container.remove();
      },
      onClose: () => {
        render(null, container);
        container.remove();
      },
    });

    // Render the modal
    render(vnode, container);
  } catch (error) {
    console.error("Error loading modal component:", error);
  }
};

// Initial load
onMounted(() => {
  if (!props.remote) {
    loadOptions();
  } else if (props.remote && props.modelValue) {
    loadOptions(props.modelValue);
  }
  calculateDropdownDirection(); // Calculate dropdown direction on mount
});

/*const updateSelectedItemFrommodal = (item_id: string | number | null | undefined) => {
  const option = items.value.find((opt) => opt.id === item_id);
  if (!option) {
    return
  }
  if (option) {
    selectedLabel.value = option.name;
  } else {
    selectedLabel.value = null;
  }
  handleSelect(option);
};*/

const dropdownDirection = ref("down");

watch(isOpen, (newValue) => {
  if (newValue) {
    nextTick(() => {
      calculateDropdownDirection();
    });
  }
});

const searchSelectInput = ref<HTMLInputElement | null>(null);
const calculateDropdownDirection = () => {
  const dropdownMaxHeight = 15 * 16; // 15rem in pixels

  if (!searchSelectInput.value) return;

  const inputRect = searchSelectInput.value.getBoundingClientRect();
  const spaceBelow = window.innerHeight - inputRect.bottom;
  const spaceAbove = inputRect.top;

  if (spaceBelow < dropdownMaxHeight && spaceAbove > spaceBelow) {
    dropdownDirection.value = "up";
  } else {
    dropdownDirection.value = "down";
  }
};

watch(
  () => props.modelValue,
  (newValue) => {
    if (!isEmpty(newValue) && typeof newValue === 'number') {
      emit("update:modelValue", Number(newValue));
    }
    if (props.remote && props.readonly && newValue) {
      loadOptions(newValue);
    }
    //updateSelectedItemFrommodal(newValue);
    if (newValue) {
      // Bilateral toString comparison — Laravel returns foreign-key columns
      // as strings (SQL Server bigint serialization), while list endpoints
      // typically return numeric ids. A strict `===` here would silently
      // overwrite a freshly-resolved selectedLabel back to null whenever
      // the modelValue updates (e.g. after setFormData on edit), making
      // already-selected dropdowns appear blank.
      const option = items.value.find(
        (opt) =>
          opt.id === newValue ||
          (opt.id != null && newValue != null &&
            opt.id.toString() === newValue.toString())
      );
      if (option) {
        selectedLabel.value = option.name;
      } else {
        selectedLabel.value = null;
      }
      //emit("update:modelValue", newValue);
    } else if (!newValue && !props.readonly) {
      //emit("update:modelValue", "");
      //emit("select", "");
      selectedLabel.value = "";
      searchQuery.value = "";
    }
  },
  { immediate: true }
);

// Watch for options prop changes
watch(
  () => props.options,
  () => {
    items.value = props.options || [];
    fullItems.value = props.options || [];
    if (props.modelValue) {
      // Bilateral toString — same reason as the modelValue watcher.
      const option = items.value.find(
        (opt) =>
          opt.id === props.modelValue ||
          (opt.id != null && props.modelValue != null &&
            opt.id.toString() === props.modelValue.toString())
      );
      if (option) {
        selectedLabel.value = option.name;
      } else {
        selectedLabel.value = null;
      }
    }
  },
  { deep: true }
);

watch(
  () => props.extra,
  () => {
    fullItems.value = [];
    items.value = [];
    //emit("update:modelValue", "");
    //emit("select", '');
    selectedLabel.value = "";
    searchQuery.value = "";
    if (props.extra && !props.remote) {
      loadOptions();
    }
  },
  { deep: true }
);

watch(
  () => props.extra1,
  () => {
    fullItems.value = [];
    items.value = [];
    //emit("update:modelValue", "");
    //emit("select", '');
    selectedLabel.value = "";
    searchQuery.value = "";
    if (props.extra1 && !props.remote) {
      loadOptions();
    }
  },
  { deep: true }
);

watch(
  () => props.extra2,
  () => {
    fullItems.value = [];
    items.value = [];
    //emit("update:modelValue", "");
    //emit("select", '');
    selectedLabel.value = "";
    searchQuery.value = "";
    if (props.extra2 && !props.remote) {
      loadOptions();
    }
  },
  { deep: true }
);

watch(
  () => selectedLabel.value,
  () => {},
  { deep: true }
);
</script>
